import {Input, InputNumber, Radio} from 'antd'
import FieldsFactory from 'components/form/FieldsFactory'
import FormActionBar from 'components/form/FormActionBar'
import {createValue} from 'components/form/utils'
import {Formik} from 'formik'
import _ from 'lodash'
import Translate from 'modules/local/Translate'
import {useAppConfig} from 'modules/local/AppConfigContext'
import useTranslate from 'modules/local/useTranslate'
import React, {useMemo} from 'react'
import {NullAsDefault, shallowDiff} from 'views/Shared'
import {createControlledFormField} from '../../AppLocals/factory/createFormField'
import {SelectWidget} from '../../AppLocals/custom/ReactSelectComponents'
import {LANGUAGE_OPTIONS} from 'components/form/fields/LanguageSelect'
import {Section} from "../../../components/form/ModernForm";

// options 4 field này lấy từ config('constants.game_*') qua apps/get-configs-by-keys (useAppConfig),
// không hardcode ở FE - đổi ở config backend là áp dụng ngay cả form biên soạn lẫn filter thư viện
const configObjectToOptions = (configObj = {}) => Object.entries(configObj).map(([value, label]) => ({value, label}))

const STATUS_OPTIONS = [
    {value: -1, label: 'pending'},
    {value: 1, label: 'open'},
    {value: 9, label: 'closed'},
]

// labelIsKey=true: label là translation key tĩnh (STATUS_OPTIONS) -> t(label)
// labelIsKey=false: label lấy thẳng từ config, đã là text hiển thị -> không qua t()
const SelectField = ({name, value, onChange, options, placeholder, labelIsKey = true}) => (
    <Translate>
        {(t) => (
            <SelectWidget
                name={name}
                value={value}
                className="w-full"
                placeholder={t(placeholder)}
                onChange={(v) => onChange(createValue(name, v))}
                options={options.map((o) => ({label: labelIsKey ? t(o.label) : o.label, value: o.value}))}
            />
        )}
    </Translate>
)

const GameTypeField = (props) => {
    const {game_types = []} = useAppConfig('game_types')
    return <SelectField {...props} labelIsKey={false} options={configObjectToOptions(game_types)} placeholder="game type"/>
}

const TopicField = (props) => {
    const {game_topics = []} = useAppConfig('game_topics')
    return <SelectField {...props} labelIsKey={false} options={configObjectToOptions(game_topics)} placeholder="topic"/>
}

const AgeGroupField = (props) => {
    const {game_age_groups = []} = useAppConfig('game_age_groups')
    return <SelectField {...props} labelIsKey={false} options={configObjectToOptions(game_age_groups)} placeholder="age group"/>
}

const DifficultyField = (props) => {
    const {game_difficulties = []} = useAppConfig('game_difficulties')
    return <SelectField {...props} labelIsKey={false} options={configObjectToOptions(game_difficulties)} placeholder="difficulty"/>
}

// dùng chung LANGUAGE_OPTIONS (vi/en) toàn hệ thống - KHÔNG qua useAppConfig/config('constants.game_*')
// như 4 field trên vì ngôn ngữ không phải taxonomy riêng của Game, xem 060_add_language_to_games.sql
const LanguageField = (props) => (
    <SelectField {...props} options={LANGUAGE_OPTIONS.map((o) => ({value: o.id, label: o.name}))} placeholder="language"/>
)

const formSchema = {
    title: {
        title: 'title',
        children: [{name: 'title', component: createControlledFormField({placeholder: 'title', Component: Input})}],
    },
    description: {
        title: 'description',
        children: [{name: 'description', component: createControlledFormField({rows: 3, placeholder: 'description', Component: Input.TextArea})}],
    },
    topic: {
        title: 'topic',
        children: [{name: 'topic', component: TopicField}],
    },
    primary_answer_type: {
        title: 'game type',
        children: [{name: 'primary_answer_type', component: GameTypeField}],
    },
    age_group: {
        title: 'age group',
        children: [{name: 'age_group', component: AgeGroupField}],
    },
    difficulty: {
        title: 'difficulty',
        children: [{name: 'difficulty', component: DifficultyField}],
    },
    language: {
        title: 'language',
        children: [{name: 'language', component: LanguageField}],
    },
    hearts_limit: {
        title: 'hearts limit',
        description: 'number of wrong answers allowed before the play session ends automatically, leave empty or 0 for unlimited',
        children: [{name: 'hearts_limit', component: ({name, value, onChange}) => (
            <InputNumber name={name} value={value} min={0} className="w-full" onChange={(v) => onChange(createValue(name, v))}/>
        )}],
    },
    total_of_questions: {
        title: 'questions per play',
        description: 'number of questions randomly drawn from the pool for each play, not used when using all questions in order',
        children: [{name: 'total_of_questions', component: ({name, value, onChange}) => (
            <InputNumber name={name} value={value} min={1} className="w-full" onChange={(v) => onChange(createValue(name, v))}/>
        )}],
    },
    point_per_question: {
        title: 'point per question',
        description: 'base point applied to every question in this game, changing this updates the point of all existing questions automatically',
        children: [{name: 'point_per_question', component: ({name, value, onChange}) => (
            <InputNumber name={name} value={value} min={0} step={0.5} className="w-full" onChange={(v) => onChange(createValue(name, v))}/>
        )}],
    },
    is_fixed_questions: {
        title: 'question source',
        children: [{name: 'is_fixed_questions', component: ({name, value, onChange}) => (
            <Translate>
                {(t) => (
                    <Radio.Group
                        name={name}
                        value={value}
                        buttonStyle="solid"
                        className="custom-radio-button"
                        onChange={(e) => onChange(createValue(name, e.target.value))}>
                        <Radio.Button value={1}>{t('use all questions in order')}</Radio.Button>
                        <Radio.Button value={0}>{t('random pick from pool')}</Radio.Button>
                    </Radio.Group>
                )}
            </Translate>
        )}],
    },
    status: {
        title: 'status',
        children: [{name: 'status', component: (props) => <SelectField {...props} options={STATUS_OPTIONS} placeholder="status"/>}],
    },
}

const FIELD_ORDER = [
    'title', 'description', 'topic', 'primary_answer_type', 'age_group', 'difficulty', 'language',
    'hearts_limit', 'total_of_questions', 'point_per_question', 'is_fixed_questions', 'status',
]

const GameFormBody = ({submitText}) => {
    const t = useTranslate()
    return (
        <div className="flex flex-col gap-4">
            <Section title={t('basic information')}>
                <FieldsFactory
                    formSchema={[
                        formSchema.title,
                        formSchema.description,
                        formSchema.primary_answer_type,
                    ]}
                />
            </Section>
            <Section>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FieldsFactory
                        formSchema={[
                            formSchema.topic,
                            formSchema.age_group,
                            formSchema.difficulty,
                            formSchema.language,
                        ]}
                    />
                </div>
            </Section>
            <Section title={t('game configuration')}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FieldsFactory
                        formSchema={[
                            formSchema.hearts_limit,
                            formSchema.total_of_questions,
                            formSchema.point_per_question,
                            formSchema.is_fixed_questions,
                        ]}
                    />
                </div>
            </Section>
            <FieldsFactory
                formSchema={[
                    formSchema.status,
                ]}
            />
            <FormActionBar submitText={submitText}/>
        </div>
    )
}

const validate = (values, t) => {
    const errors = {}
    if (_.isEmpty(values.title)) errors.title = t('required field')
    if (_.isEmpty(values.primary_answer_type)) errors.primary_answer_type = t('required field')
    // hearts_limit=0/rỗng = không giới hạn, không cần so - còn có set thì phải nhỏ hơn total_of_questions,
    // nếu không tính năng "hết tim thì dừng sớm" sẽ vô nghĩa (không bao giờ trigger được, xem Game::isHeartsLimitValid)
    if (values.hearts_limit && values.total_of_questions && Number(values.hearts_limit) >= Number(values.total_of_questions)) {
        errors.hearts_limit = t('hearts limit must be less than questions per play')
    }
    return errors
}

export const CreateGameForm = ({onSubmit = NullAsDefault}) => {
    const t = useTranslate()
    return (
        <Formik
            initialValues={{
                title: undefined,
                description: undefined,
                primary_answer_type: undefined,
                topic: undefined,
                age_group: undefined,
                difficulty: undefined,
                language: 'vi',
                hearts_limit: 3,
                total_of_questions: 10,
                point_per_question: 1,
                is_fixed_questions: 1,
                status: -1,
            }}
            validate={(values) => validate(values, t)}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            onSubmit={(values) => onSubmit(_.omitBy(values, _.isUndefined))}>
            <GameFormBody submitText="create"/>
        </Formik>
    )
}

export const EditGameForm = ({item, onSubmit = NullAsDefault}) => {
    const t = useTranslate()
    const initialValues = useMemo(() => ({...item}), [item])
    return (
        <Formik
            validate={(values) => validate(values, t)}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={initialValues}
            onSubmit={(values) => {
                const change = shallowDiff(values, initialValues)
                onSubmit(_.omitBy(change, _.isUndefined))
            }}>
            <GameFormBody submitText="save"/>
        </Formik>
    )
}
